<script setup lang="ts">
import { format } from 'date-fns'

const { initiallyOpenItem } = defineProps<{
  title?: StoryBlokHeading
  subtitle?: StoryBlokRichText
  showNumbers?: boolean
  removeBackground?: boolean
  items: {
    type: 'normal' | 'image'
    title: string
    logo?: StoryBlokImage
    text: any
    headerText?: any
    image?: StoryBlokImage
    icon?: StoryBlokIcon
    datePicker?: any
    addUpMonths?: string
    substractDays?: string
    textToShow?: string
  }[]
  link?: string
  toggleText?: string
  initiallyOpenItem?: string
}>()

const locale = useAppConfig().locale

const resultDate = ref<any>(null)

function handleChangeDate(date: Date, addUpMonths?: number, substractDays?: number) {
  const pickedDate = new Date(date)

  if (addUpMonths) pickedDate.setMonth(pickedDate.getMonth() + addUpMonths)
  if (substractDays) pickedDate.setDate(pickedDate.getDate() - substractDays)

  resultDate.value = format(pickedDate, 'do MMMM yyyy', {
    locale,
  })
}

const open = ref(initiallyOpenItem ? parseInt(initiallyOpenItem) - 1 : Number.NaN)

function openItem(index: number) {
  open.value = index
}

function closeItem(index: number) {
  if (index === open.value) return (open.value = Number.NaN)
  open.value = index
}
</script>

<template>
  <div>
    <div class="mx-auto max-w-[1160px]">
      <StoryblokHeading v-if="title" :title="title" class="sm:text-center" />
      <StoryblokRichText v-if="useHasContent(subtitle)" :document="subtitle" class="sm:text-center text-gray-dark" />
      <div class="flex flex-col gap-10px sm:gap-5 w-full sm:max-w-[780px] mt-6 mx-auto">
        <div
          v-for="(item, index) in items" v-editable="item"
          class="rounded-xl cursor-pointer p-5 hover:border-primary transition-colors"
          :class="[index === open && 'border-primary', !removeBackground && 'bg-white border-1']"
          @click="index === open ? closeItem(index) : openItem(index)"
        >
          <div class="flex justify-between items-center">
            <div class="flex items-center">
              <div v-if="showNumbers">
                <div class="items-center bg-primary w-25px rounded-full text-white text-center mr-2">
                  {{ index + 1 }}
                </div>
              </div>
              <div v-if="item.logo?.filename" class="w-185px hidden md:block">
                <StoryblokImage :src="item.logo" />
              </div>
              <h3>{{ item.title }}</h3>
            </div>
            <AppIcon
              size="24" msize="20" :class="[index === open && 'rotate-180']"
              class="text-primary ml-2 duration-300 transition transform" icon="far-angle-down"
              @click.stop="closeItem(index)"
            />
          </div>
          <CollapseTransition>
            <div
              v-show="index === open"
              class="pt-3 transition duration-700 transform scale-x-100 text-large break-words w-full"
            >
              <div v-if="item.type === 'image'">
                <StoryblokImage v-if="item.image?.filename" class="flex w-150px my-3 sm:hidden" :src="item.image" />
                <AppIcon v-else-if="item.icon" msize="60" class="flex text-primary my-3 sm:hidden" v-bind="item.icon" />
                <div class="flex justify-between pb-5">
                  <StoryblokRichText :document="item.headerText" class="w-full" />
                  <StoryblokImage v-if="item.image?.filename" class="hidden w-200px my-3 sm:flex" :src="item.image" />
                  <AppIcon v-else-if="item.icon" v-bind="item.icon" size="80" class="hidden w-200px my-3 text-primary sm:flex" />
                </div>
                <StoryblokRichText :document="item.text" class="w-full" />
              </div>
              <div v-else>
                <StoryblokRichText :document="item.text" class="w-full" />
                <div v-if="item.datePicker?.length" class="flex flex-col gap-2 w-full pt-4">
                  <FormSimple class="w-full sm:w-max-1/3" :fields="item.datePicker">
                    <FormFieldDatepicker
                      name="datePicker"
                      @update:model-value="(date) => handleChangeDate(date, Number(item.addUpMonths), Number(item.substractDays))"
                    />
                  </FormSimple>
                  <div v-if="resultDate && item.textToShow?.length">
                    {{ item.textToShow }}
                    <span class="font-bold">
                      {{ resultDate }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </CollapseTransition>
        </div>
        <div v-if="link" class="mx-auto">
          <StoryblokButton icon-end="fas-arrow-right" icon-size="small" render="link" class="font-semibold">
            <StoryblokLink :to="link">
              {{ toggleText || 'Zobrazit všechny dotazy' }}
            </StoryblokLink>
          </StoryblokButton>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped></style>
